import BaseAlert from '@/components/base/BaseAlert';
import BaseSection from '@/components/base/BaseSection';
import BaseTable from '@/components/base/BaseTable';
import BaseTag from '@/components/base/BaseTag';
import DetailTable from '@/components/composite/DetailTable';
import { useIntl } from '@/i18n';
import type {
  AdminAccountDetail,
  AdminPermissionCode,
  AdminPermissionDetail,
} from '@/types';

interface AdminPermissionContentProps {
  detail: AdminAccountDetail;
  value: AdminPermissionDetail;
}

/** 显示正式权限并集和来源，不从摘要反推或补齐权限。 */
export default function AdminPermissionContent({
  detail,
  value,
}: AdminPermissionContentProps) {
  const intl = useIntl();
  const t = (key: string) =>
    intl.formatMessage({ id: 'adminAcct.detail.' + key });
  const names = (nodes: AdminPermissionCode[], allKey: string) =>
    nodes.some((node) => node.code === '*')
      ? t(allKey)
      : nodes.map((node) => node.name).join('、') || '—';
  const scope =
    value &&
    (['all', 'dept', 'self'].includes(value.scopeName)
      ? intl.formatMessage({ id: `scope.${value.scopeName}` })
      : value.scopeName);
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {' '}
      <BaseAlert type="info" showIcon title={t('unionNotice')} />
      <DetailTable
        items={[
          {
            key: 'email',
            label: intl.formatMessage({ id: 'adminAcct.field.loginEmail' }),
            children: value.loginId,
          },
          {
            key: 'type',
            label: intl.formatMessage({
              id: 'adminAcct.detail.accountType',
            }),
            children: (
              <BaseTag color={detail.accountKind === 'tenant' ? 'red' : 'blue'}>
                {t(detail.accountKind === 'tenant' ? 'ownerKind' : 'adminKind')}
              </BaseTag>
            ),
          },
          {
            key: 'archive',
            label: t('boundArchive'),
            children: value.boundArchiveNo
              ? `${value.boundArchiveName ?? ''} ${value.boundArchiveNo}`
              : t('unassociated'),
          },
          {
            key: 'status',
            label: intl.formatMessage({
              id: 'adminAcct.detail.accountStatus',
            }),
            children: intl.formatMessage({
              id: `adminAcct.status.${value.accountStatus}`,
            }),
          },
          {
            key: 'scope',
            label: t('scope'),
            children: (
              <>
                {scope}
                {value.scopeFrom && `（${value.scopeFrom}）`}
              </>
            ),
          },
          {
            key: 'pages',
            label: t('pages'),
            children: names(value.pages, 'allPages'),
          },
          {
            key: 'buttons',
            label: t('buttons'),
            children: names(value.buttons, 'allButtons'),
          },
        ]}
      />
      {detail.permission &&
        !detail.permission.allPages &&
        detail.permission.pageCount === 0 &&
        !detail.posts.length && (
          <BaseAlert type="warning" showIcon title={t('bare')} />
        )}
      <BaseSection title={t('permissionSources')}>
        <BaseTable<AdminPermissionDetail['from'][number]>
          rowKey={(_, index) => String(index)}
          dataSource={value.from}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: t('noEffectivePosts') }}
          columns={[
            {
              title: intl.formatMessage({ id: 'adminAcct.field.posts' }),
              render: (_, row) => (
                <>
                  <b>{row.postName}</b>
                  <div className="text-xs text-secondary">
                    {t(
                      row.source === 'fromTitle'
                        ? 'source.title'
                        : 'source.grant',
                    )}{' '}
                    · {row.basis}
                    {row.expireText && ` · ${row.expireText}`}
                  </div>
                </>
              ),
            },
            {
              title: t('sourcePages'),
              render: (_, row) => names(row.pages, 'allPages'),
            },
            {
              title: t('sourceButtons'),
              render: (_, row) => names(row.buttons, 'allButtons'),
            },
          ]}
        />
      </BaseSection>
      {!!value.expired.length && (
        <BaseSection
          title={t('expiredPosts')}
          extra={
            <span className="text-xs text-secondary">{t('expiredHint')}</span>
          }
        >
          <BaseTable<AdminPermissionDetail['expired'][number]>
            rowKey={(_, index) => String(index)}
            dataSource={value.expired}
            pagination={false}
            scroll={{ x: 'max-content' }}
            columns={[
              {
                title: intl.formatMessage({ id: 'adminAcct.field.posts' }),
                dataIndex: 'postName',
              },
              {
                title: t('source'),
                dataIndex: 'source',
                render: (source) =>
                  t(source === 'fromTitle' ? 'source.title' : 'source.grant'),
              },
              { title: t('expireReason'), dataIndex: 'expireReason' },
            ]}
          />
        </BaseSection>
      )}
    </div>
  );
}
